import React, { useEffect, useRef, useState } from "react";

import moment from "moment";
import { useSharedRangePicker } from "@/components/DatePickers/shared/hooks/useSharedRangePicker";
import {
  SLIDE_DIRECTION,
  VIEW_MODE
} from "@/components/DatePickers/shared/types";
import { useResizeObserver } from "@usefy/use-resize-observer";

interface UseBaseDayPickerProps {
  minDate?: moment.Moment;
  maxDate?: moment.Moment;
  disabled?: boolean;
  timeInput?: React.ReactNode;
}

export const useBaseDayPicker = ({
  minDate,
  maxDate,
  disabled,
  timeInput
}: UseBaseDayPickerProps) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  const {
    year,
    setYear,
    viewMode,
    setViewMode,
    viewVisible,
    slideClass,
    cancelHoverClear,
    animateSlide,
    animateFadeView
  } = useSharedRangePicker({
    initialYear: currentYear,
    initialViewMode: VIEW_MODE.Day
  });

  const [month, setMonth] = useState(currentMonth);
  const [open, setOpen] = useState(false);
  const [maxPickerHeight, setMaxPickerHeight] = useState<number | null>(null);

  const anchorRef = useRef<HTMLDivElement>(null);

  const PICKER_HEIGHTS: Record<
    VIEW_MODE,
    { withTime: number; withoutTime: number }
  > = {
    [VIEW_MODE.Day]: { withTime: 450, withoutTime: 296 },
    [VIEW_MODE.Month]: { withTime: 380, withoutTime: 226 },
    [VIEW_MODE.Year]: { withTime: 380, withoutTime: 226 }
  };

  const check = () => {
    if (!anchorRef.current || !open) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const fixedPickerHeight =
      PICKER_HEIGHTS[viewMode][timeInput ? "withTime" : "withoutTime"];

    setMaxPickerHeight(prev => {
      const newHeight = spaceBelow < fixedPickerHeight ? spaceBelow : null;
      return prev === newHeight ? prev : newHeight;
    });
  };

  const { ref: pickerRef } = useResizeObserver<HTMLDivElement>({
    onResize: check
  });

  useEffect(() => {
    if (!open) return;
    setTimeout(check, 0);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [open]);

  const openPicker = (onOpen?: () => void) => {
    if (disabled) return;
    setViewMode(VIEW_MODE.Day);
    setOpen(true);
    onOpen?.();
  };

  const closePicker = () => {
    setOpen(false);
    cancelHoverClear();
  };

  const handleNavigateBack = () => {
    animateSlide(SLIDE_DIRECTION.Right, () => {
      if (month === 0) {
        setYear(prev => prev - 1);
        setMonth(11);
      } else {
        setMonth(prev => prev - 1);
      }
    });
  };

  const handleNavigateForward = () => {
    animateSlide(SLIDE_DIRECTION.Left, () => {
      if (month === 11) {
        setYear(prev => prev + 1);
        setMonth(0);
      } else {
        setMonth(prev => prev + 1);
      }
    });
  };

  const handleHeaderClick = () => {
    animateFadeView(() => {
      if (viewMode === VIEW_MODE.Day) setViewMode(VIEW_MODE.Month);
      else if (viewMode === VIEW_MODE.Month) setViewMode(VIEW_MODE.Year);
      else setViewMode(VIEW_MODE.Day);
    });
  };

  const handleMonthSelect = (selectedMonth: number) => {
    animateFadeView(() => {
      setMonth(selectedMonth);
      setViewMode(VIEW_MODE.Day);
    });
  };

  const handleYearSelect = (selectedYear: number) => {
    animateFadeView(() => {
      setYear(selectedYear);
      setViewMode(VIEW_MODE.Month);
    });
  };

  const getHeaderLabel = () => {
    const monthName = moment().month(month).format("MMMM");
    return viewMode === VIEW_MODE.Day ? `${monthName} ${year}` : `${year}`;
  };

  const isAtMinMonth = minDate
    ? year < minDate.year() ||
      (year === minDate.year() && month <= minDate.month())
    : false;

  const isAtMaxMonth = maxDate
    ? year > maxDate.year() ||
      (year === maxDate.year() && month >= maxDate.month())
    : false;

  return {
    year,
    setYear,
    month,
    setMonth,
    currentYear,
    currentMonth,
    open,
    viewMode,
    viewVisible,
    slideClass,
    maxPickerHeight,
    anchorRef,
    pickerRef,
    isAtMinMonth,
    isAtMaxMonth,
    getHeaderLabel,
    openPicker,
    closePicker,
    cancelHoverClear,
    handleNavigateBack,
    handleNavigateForward,
    handleHeaderClick,
    handleMonthSelect,
    handleYearSelect
  };
};
